import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../store/auth';
import { useLiveEvents } from '../hooks/useLiveEvents';
import BackBtn from '../components/BackBtn';

export default function Recordings() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [openSession, setOpenSession] = useState(null);
  const [frameIdx, setFrameIdx] = useState(0);
  const [recording, setRecording] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/devices/${deviceId}/recordings`);
      setSessions(data);
    } catch { }
  };

  useEffect(() => { load(); }, [deviceId]);

  useLiveEvents((e) => {
    if (e.device_id !== deviceId) return;
    if (e.event === 'recording_started') setRecording(true);
    if (e.event === 'recording_stopped') { setRecording(false); load(); }
  });

  const token = localStorage.getItem('token');
  const frameUrl = (filename) => `/files/recording/${filename}?token=${token}`;

  const openViewer = (session) => {
    setOpenSession(session);
    setFrameIdx(0);
  };

  const deleteSession = async (e, sessionId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this recording session?')) return;
    try {
      await api.delete(`/devices/${deviceId}/recordings/${sessionId}`);
      setSessions(prev => prev.filter((_, i) => i !== sessionId));
      if (openSession?.session_id === sessionId) setOpenSession(null);
    } catch {
      alert('Delete failed');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <BackBtn onClick={() => navigate(`/device/${deviceId}`)} />
        <h2 style={{ fontWeight: 700 }}>Screen Recordings</h2>
        {recording && (
          <span style={{ fontSize: 12, padding: '3px 10px', background: '#ef444420',
            color: '#ef4444', borderRadius: 12, border: '1px solid #ef444440' }}>
            ● Recording in progress…
          </span>
        )}
        <button onClick={load} style={btnStyle}>Refresh</button>
        <span style={{ color: 'var(--muted)', fontSize: 12 }}>
          {sessions.length} session{sessions.length !== 1 ? 's' : ''}
        </span>
      </div>

      {sessions.length === 0 ? (
        <p style={{ color: 'var(--muted)', padding: 40 }}>No recordings yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sessions.map((s) => (
            <div key={s.session_id} onClick={() => openViewer(s)}
              style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 8, padding: 16, cursor: 'pointer', display: 'flex',
                alignItems: 'center', gap: 16 }}>
              {/* Thumbnail */}
              <img src={frameUrl(s.frames[0]?.filename)} alt=""
                style={{ width: 160, height: 90, objectFit: 'cover', borderRadius: 4,
                  border: '1px solid var(--border)', flexShrink: 0 }}
                onError={e => { e.target.style.display = 'none'; }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {s.started_at ? new Date(s.started_at + 'Z').toLocaleString() : 'Unknown start'}
                  {' → '}
                  {s.ended_at ? new Date(s.ended_at + 'Z').toLocaleTimeString() : ''}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                  {s.frame_count} frames
                </div>
              </div>
              <button onClick={(e) => deleteSession(e, s.session_id)}
                style={{ ...btnStyle, color: '#ef4444', borderColor: '#ef444440' }}>
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Frame viewer modal */}
      {openSession && (
        <div onClick={() => setOpenSession(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.95)',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', zIndex: 100,
        }}>
          <div onClick={e => e.stopPropagation()}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
              maxWidth: '95vw' }}>
            <img src={frameUrl(openSession.frames[frameIdx]?.filename)} alt=""
              style={{ maxWidth: '90vw', maxHeight: '75vh', borderRadius: 6 }} />

            {/* Frame counter + nav */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#fff' }}>
              <button disabled={frameIdx === 0}
                onClick={() => setFrameIdx(i => Math.max(0, i - 1))}
                style={navBtn}>‹ Prev</button>
              <span style={{ fontSize: 13 }}>
                {frameIdx + 1} / {openSession.frames.length}
              </span>
              <button disabled={frameIdx === openSession.frames.length - 1}
                onClick={() => setFrameIdx(i => Math.min(openSession.frames.length - 1, i + 1))}
                style={navBtn}>Next ›</button>
            </div>

            {/* Scrubber */}
            <input type="range" min={0} max={openSession.frames.length - 1}
              value={frameIdx} onChange={e => setFrameIdx(Number(e.target.value))}
              style={{ width: 400, maxWidth: '80vw' }} />

            <div style={{ display: 'flex', gap: 8 }}>
              <a href={frameUrl(openSession.frames[frameIdx]?.filename)} download
                style={{ ...btnStyle, color: '#fff', background: '#ffffff22',
                  textDecoration: 'none', textAlign: 'center' }}>
                ↓ Download frame
              </a>
              <button onClick={() => setOpenSession(null)}
                style={{ ...btnStyle, color: 'var(--muted)' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btnStyle = {
  padding: '6px 14px', background: 'transparent', border: '1px solid var(--border)',
  borderRadius: 6, color: 'var(--muted)', cursor: 'pointer',
};
const navBtn = {
  padding: '6px 16px', background: '#ffffff22', border: '1px solid #ffffff44',
  borderRadius: 6, color: '#fff', cursor: 'pointer',
};
